import YAML, { isMap, isSeq, YAMLMap, YAMLSeq, Document, Node } from 'yaml';

/**
 * Insert **or** replace a screen object (identified by its `id`)
 * into the course YAML while preserving all unrelated comments.
 *
 * @param courseYaml – full course YAML string (compiled or draft)
 * @param screenYaml – YAML for one **complete** screen object
 *
 * @returns updated YAML string
 */
export default function upsertPatch(
  courseYaml: string,
  screenYaml: string
): string {
  // Parse main document & patch (both keep CST → comments survive)
  const doc: Document.Parsed = YAML.parseDocument(courseYaml);
  const screenDoc = YAML.parseDocument(screenYaml.trim());
  const screenNode = screenDoc.contents as Node;

  if (!isMap(screenNode)) {
    throw new Error('screenYaml must be a YAML mapping (object)');
  }

  // Extract id via plain JS object (safest, covers all scalar types)
  const screenObj = YAML.parse(screenYaml.trim()) as { id?: string };
  const screenId = screenObj?.id;
  if (!screenId) {
    throw new Error('screenYaml is missing an id property');
  }

  // Ensure root-level 'patches' mapping exists
  let patches = doc.get('patches') as YAMLMap | undefined;
  if (!patches) {
    patches = doc.createNode({}) as YAMLMap;
    // Append the 'patches' map at the end to minimise diff noise
    doc.set('patches', patches);
  }

  // A screen added via the "add page" feature lives under an advanced-insert
  // key (`> #parent` / `< #parent`) whose child screen carries its own id.
  // Editing that screen must update the SAME advanced key, not spawn a duplicate
  // plain `screenId:` key — otherwise the inserted screen is lost on reload.
  const advancedInsertKey = patches.items.find(pair => {
    const key = String(pair.key);
    if (!/^(<|>)\s*#?/.test(key)) return false;
    const value = pair.value;
    return (
      isSeq(value) &&
      isMap(value.items[0]) &&
      (value.items[0] as YAMLMap).get('id') === screenId
    );
  })?.key;

  // A bare `screenId:` key replaces a base screen; an advanced-insert key edits
  // an added page in place. If the screen turns out to resolve to neither on
  // reload, the compiler silently skips that patch (applyPatch swallows the
  // lookup error) — the edit is dropped, nothing else breaks — so there's no
  // corruption to guard against here.
  const targetKey = advancedInsertKey ?? screenId;

  // Get or create the sequence for this screen id
  let seq = patches.get(targetKey) as YAMLSeq | undefined;
  if (!seq || !isSeq(seq)) {
    seq = doc.createNode([]) as YAMLSeq;
    patches.set(targetKey as any, seq);
  }

  if (seq.items.length === 0) {
    seq.add(screenNode);
  } else {
    seq.items[0] = screenNode;
  }

  return doc.toString();
}
